import { CostType, money } from './commission'

/**
 * Standard costs - Winnie, 7 Sep: "where a bill is missing, accrue a standard cost -
 * standard inspection fee, freight rate by distance band - flag it estimated - unconfirmed,
 * and reconcile it against the actual when the invoice arrives."
 *
 * This file is the choosing. It takes a rate card and a question ("what does an inspection
 * normally cost?", "what is freight for 340km?") and answers it, or says plainly that it
 * cannot. What it never does is guess.
 *
 * That refusal is the important part. An accrual is a number that goes onto a real vehicle,
 * feeds a landed cost, and moves somebody's commission. A rate card with no inspection fee
 * on it should produce no inspection accrual - not a plausible-looking $150 that nobody
 * chose and everybody later assumes was real.
 */

export interface StandardCost {
  id: string
  cost_type: CostType
  label: string
  basis: 'flat' | 'distance_band'
  band_min_km: number | null
  band_max_km: number | null
  amount: number
  active: boolean
}

export type RateCode =
  | 'flat'
  | 'band'
  | 'none_configured'
  | 'distance_required'
  | 'no_band_matches'
  | 'ambiguous'

export interface RateResult {
  /** The amount to accrue, or null when there is nothing defensible to accrue. */
  amount: number | null
  code: RateCode
  /** The rate that produced it, so the accrual can say where the number came from. */
  rate: StandardCost | null
  /** Plain English, for the screen. Null when a rate was found cleanly. */
  message: string | null
}

const activeFor = (rates: StandardCost[] | null | undefined, costType: CostType) =>
  (rates || []).filter((r) => r.active && r.cost_type === costType)

/**
 * Does a distance fall in a band?
 *
 * Bands are half-open - `min <= km < max` - so 0-100 and 100-500 meet at 100 without both
 * claiming it. A null max is the open-ended top band.
 */
export function bandMatches(rate: StandardCost, km: number): boolean {
  if (rate.basis !== 'distance_band') return false
  const min = Number(rate.band_min_km)
  if (!Number.isFinite(min) || km < min) return false
  if (rate.band_max_km === null || rate.band_max_km === undefined) return true
  return km < Number(rate.band_max_km)
}

/**
 * The standard cost for one cost type, given a distance where distance is what decides it.
 *
 * Every path that returns null returns a reason with it, because "no accrual" needs to be
 * explainable on screen. "We could not price the freight because nobody has told us how far
 * it is" is a fixable problem; a silent absence is not.
 */
export function rateFor(
  rates: StandardCost[] | null | undefined,
  costType: CostType,
  distanceKm?: number | null,
): RateResult {
  const candidates = activeFor(rates, costType)
  if (candidates.length === 0) {
    return {
      amount: null,
      code: 'none_configured',
      rate: null,
      message: 'No standard rate has been set for this cost, so nothing is accrued for it.',
    }
  }

  const bands = candidates.filter((r) => r.basis === 'distance_band')
  const flats = candidates.filter((r) => r.basis === 'flat')
  /* Number(null) is 0, and 0 is a perfectly good distance - which is how an unrecorded
     distance quietly becomes the 0-100km rate. Absence has to be tested before conversion,
     not after. */
  const given = distanceKm !== null && distanceKm !== undefined && String(distanceKm).trim() !== ''
  const km = Number(distanceKm)
  const haveKm = given && Number.isFinite(km) && km >= 0

  if (bands.length > 0 && haveKm) {
    const hit = bands.filter((r) => bandMatches(r, km))
    if (hit.length === 1) {
      return { amount: money(hit[0].amount), code: 'band', rate: hit[0], message: null }
    }
    if (hit.length > 1) {
      return {
        amount: null,
        code: 'ambiguous',
        rate: null,
        message: `${hit.length} distance bands both cover ${km}km. Fix the overlap on the rate card before this can be accrued.`,
      }
    }
    /* Bands exist but none reaches this distance. A flat rate is still a legitimate answer
       if one is set; otherwise this is a gap in the rate card, and saying so is the help. */
    if (flats.length === 0) {
      return {
        amount: null,
        code: 'no_band_matches',
        rate: null,
        message: `No distance band covers ${km}km. Add a band that does, and this will accrue.`,
      }
    }
  }

  if (bands.length > 0 && !haveKm && flats.length === 0) {
    return {
      amount: null,
      code: 'distance_required',
      rate: null,
      message:
        'This cost is priced by distance and no distance is recorded on the vehicle, so it is not accrued.',
    }
  }

  if (flats.length === 1) {
    return { amount: money(flats[0].amount), code: 'flat', rate: flats[0], message: null }
  }
  if (flats.length > 1) {
    return {
      amount: null,
      code: 'ambiguous',
      rate: null,
      message: `${flats.length} standard rates are active for this cost. Leave one active so there is a single answer.`,
    }
  }

  return {
    amount: null,
    code: 'none_configured',
    rate: null,
    message: 'No standard rate has been set for this cost, so nothing is accrued for it.',
  }
}

/* --------------------------------------------------------------------------------------
 * Reconciliation on arrival
 * ------------------------------------------------------------------------------------ */

export interface ReconcileResult {
  variance: number
  /** True when the real bill is bigger than what was accrued. */
  under: boolean
  message: string
}

/**
 * What the real invoice did to the accrual.
 *
 * Deliberately arithmetic and a sentence, nothing more. Whether the variance is large enough
 * to move a commission is `isMaterial`'s question, and whether the commission may still move
 * at all is `reopenDecision`'s. Three separate judgements, kept separate, because the first
 * is a fact and the other two are policy.
 */
export function reconcile(estimated: number | null | undefined, actual: number | null | undefined): ReconcileResult | null {
  if (estimated === null || estimated === undefined) return null
  const e = money(Number(estimated) || 0)
  const a = money(Number(actual) || 0)
  const variance = money(a - e)
  if (variance === 0) {
    return { variance: 0, under: false, message: `The invoice came in at the accrued ${aud(e)} exactly.` }
  }
  return {
    variance,
    under: variance > 0,
    message:
      variance > 0
        ? `The invoice is ${aud(variance)} more than the ${aud(e)} accrued.`
        : `The invoice is ${aud(Math.abs(variance))} less than the ${aud(e)} accrued.`,
  }
}

function aud(n: number): string {
  return `${n < 0 ? '-$' : '$'}${Math.abs(n).toLocaleString('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}
